/* eslint-disable no-param-reassign */
/* eslint-disable react/prop-types */
/* eslint-disable no-undef */
/* eslint-disable import/prefer-default-export */
import React from 'react';
import { Pagination, Toolbar, SaveButton } from 'react-admin';
import download from "downloadjs";
import SelectValues from './constants/selectValues';
import typeDocumentChoice from './constants/choices/typeDocumentChoice';

export const formatCep = cep => {
  if (!cep || cep.trim() === "") return "";
  const normalizedCep = cep.replace(/\D/g, '');
  return normalizedCep.padStart(8, '0');
};

/**
 * Normaliza um documento removendo caracteres especiais e letras/números extras
 * Extrai as 2 primeiras letras (ignorando números no meio) e todos os números (ignorando letras no meio)
 * @param {string} document - documento a ser normalizado
 * @returns {string} - documento normalizado
 */
export const normalizeDocument = document => {
  if (!document || typeof document !== 'string') return '';
  
  const cleaned = document.replace(/[^a-zA-Z0-9]/g, '');
  const allLetters = cleaned.match(/[a-zA-Z]/g) || [];
  const letters = allLetters.slice(0, 2).join('');
  const numbers = (cleaned.match(/[0-9]/g) || []).join('');
  
  return letters.toUpperCase() + numbers;
};

/**
 * Valida se o documento está no formato correto (2 letras seguidas de números)
 * @param {string} document - documento a ser validado
 * @returns {boolean} - true se válido, false caso contrário
 */
export const validateDocumentFormat = document => {
  if (!document || typeof document !== 'string') return false;
  return /^[A-Za-z]{2}\d+$/.test(document);
};

/**
 * Formata um documento aplicando normalização caso necessário
 * @param {string} document - documento a ser formatado
 * @returns {string} - documento formatado
 */
export const formatDocument = document => {
  if (!document) return '';
  
  if (validateDocumentFormat(document)) {
    return document.toUpperCase();
  }
  return normalizeDocument(document);
}

export const lookupCep = async (cep) => {
  const response = await fetch(`https://viacep.com.br/ws/${cep.replace('-', '')}/json/`);

  if (response.ok) {
    const data = await response.json();
    if (data.erro) {
      return {
        success: false,
        error: 'CEP Inválido',
      };
    }
    return ({
      success: true,
      data: {
        cep,
        address: data.logradouro,
        city: data.localidade,
        state: data.uf,
      },
    });
  }
  return ({ success: false, error: 'Ocorreu um erro, tente novamente' });
};

export const downloadReceipt = async (route, fileName) => {
  const data = await fetch(`${process.env.REACT_APP_API_URL}/${route}`, {
    method: "GET",
    headers: new Headers({
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    }),
  })

  const blob = await data.blob()
  return download(blob, fileName)
}

export const formatCpf = cpf => cpf.replace(/[^0-9]/g, '').padStart(11, '0');

export const validateCpf = (cpf, type = null) => {
  let sum;
  let rest;
  sum = 0;

  cpf = cpf.replace(/[^0-9]/g, '').padStart(11, '0'); // eslint-disable-line

  if (cpf === '00000000000' && type === '0') {
    return true;
  }

  if (cpf === '00000000000') return false;

  for (let i = 1; i <= 9; i += 1) sum += parseInt(cpf.substring(i - 1, i), 10) * (11 - i);
  rest = (sum * 10) % 11;

  if ((rest === 10) || (rest === 11)) rest = 0;
  if (rest !== parseInt(cpf.substring(9, 10), 10)) return false;

  sum = 0;
  for (let i = 1; i <= 10; i += 1) sum += parseInt(cpf.substring(i - 1, i), 10) * (12 - i);
  rest = (sum * 10) % 11;

  if ((rest === 10) || (rest === 11)) rest = 0;
  if (rest !== parseInt(cpf.substring(10, 11), 10)) return false;

  return true;
};

export const ListPagination = props => (
  <Pagination rowsPerPageOptions={[25, 50, 100, 250, 500]} {...props} />
);

export const validateTypeUser = (typeUser) => {
  if (typeUser == null || String(typeUser).trim() === '') return false;
  const s = String(typeUser).trim();
  if (!/^\d+$/.test(s)) return false;  
  const num = Number.parseInt(s, 10);
  return typeDocumentChoice.some(item => item.id === num);
};

export const validateEmail = email => /\S+@\S+\.\S+/.test(email);

export const validateDate = date => /^\d{2}\/\d{2}\/\d{2,4}$/.test(date);

export const validateCRM = crm => /^[A-Za-z]{2}\d+$/.test(crm) &&
  (SelectValues.states.includes(crm.slice(0, 2).toLocaleUpperCase()) || crm.slice(0, 2).toLocaleUpperCase() === 'RX');

/**
 * Valida e normaliza um documento seguindo o fluxo completo:
 * 1. Valida se está no formato correto (2 letras + números)
 * 2. Normaliza o documento caso necessário
 * 3. Valida se é um CRM com UF válida
 * 
 * @param {string} document - documento a ser validado e normalizado
 * @returns {Object} - objeto com documento normalizado e status de validação
 * @returns {string} returns.normalizedDocument - documento após normalização
 * @returns {boolean} returns.isValid - indica se o documento é válido (formato correto após normalização)
 * @returns {string} returns.error - mensagem de erro caso inválido
 */
export const validateAndNormalizeDocument = document => {
  const normalizedDocument = formatDocument(document);
  
  if (!validateDocumentFormat(normalizedDocument)) {
    return {
      normalizedDocument,
      isValid: false,
      error: `Documento com formato inválido: ${normalizedDocument}`,
    };
  }

  if (!validateCRM(normalizedDocument)) {
    return {
      normalizedDocument,
      isValid: false,
      error: `Documento com UF inválida (não é um estado brasileiro válido): ${normalizedDocument}`,
    };
  }
  
  return {
    normalizedDocument,
    isValid: true,
    error: null,
  };
};

export const downloadCsv = (csv, filename) => {
  const fakeLink = document.createElement('a');
  fakeLink.style.display = 'none';
  document.body.appendChild(fakeLink);
  const blob = new Blob(['\ufeff', csv], { type: 'text/csv' });
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    // Manage IE11+ & Edge
    window.navigator.msSaveOrOpenBlob(blob, `${filename}.csv`);
  } else {
    fakeLink.setAttribute('href', URL.createObjectURL(blob));
    fakeLink.setAttribute('download', `${filename}.csv`);
    fakeLink.click();
  }
};

export const CustomToolbar = props => (
  <Toolbar {...props}>
    <SaveButton />
    {/* <DeleteButton undoable={false} /> */}
  </Toolbar>
);

/**
 * Validate if the given phone string is valid
 * @param {string} phone - phone to be validated
 * @returns {boolean}
 */
export const validatePhone = phone => /^\d{10,11}$/.test(phone);

/**
 * Validate if the given occupation is valid
 * @param {string} occupation - occupation to be validated
 * @returns {boolean}
 */
export const validateOccupation = occupation => /^(representante|gerente)$/.test(occupation);

/**
 * Get human readable error message from the column errors array
 * @param {Array<{column: string}>} errors - Column errors array
 * @returns {string}
 */
export const getReadableColumnsErrorMessage = errors => errors
  .map(({ column }) => `coluna: ${column}`)
  .join('\r\n');

/**
 * Get human readable error message from the rows errors array
 * @param {Array<{line: number, column: string, value?: string}>} errors - Rows errors array
 * @returns {string}
 */
export const getReadableRowsErrorMessage = errors => errors
  .map(({ line, column, value }) => `linha: ${line}, coluna: ${column}, valor: ${value}`)
  .join('\r\n');

/**
 * Validate if the given password meets all requirements
 * @param {string} password - password to be validated
 * @returns {boolean}
 */
export const validatePassword = password =>
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/.test(password);
